import { FloatingPortal } from '@floating-ui/react';
import {
  type Alignment,
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
} from '@floating-ui/react-dom';
import { Popover } from '@headlessui/react';
import type { Column, Table } from '@tanstack/table-core';
import clsx from 'clsx';
import { match } from 'ts-pattern';
import { useTheme } from '#ui/hooks/useTheme';
import Button from '../../Button/Button';
import { LazyAnimatePresence, m } from '../../Transitions/LazyAnimatePresence';
import { CheckboxFilter } from './CheckboxFilter';

interface ColumnFilterPopoverProps<T> {
  label: React.ReactNode;
  alignment?: Alignment;
  onChange?: (value: string | string[]) => void;
  column?: Column<T, unknown>;
  data: T[];
  table: Table<T>;
}

export function ColumnWithFilter<T>(props: ColumnFilterPopoverProps<T>) {
  const isKunde = useTheme() === 'kunde';
  const { refs, floatingStyles } = useFloating({
    middleware: [
      autoPlacement({
        alignment: props.alignment || 'start',
        allowedPlacements: [
          'bottom-end',
          'bottom-start',
          'top-end',
          'top-start',
        ],
      }),
      offset(() => {
        return 15;
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const values = props.column?.getFilterValue();
  const filteredRows = Array.from(props.table.getCoreRowModel().rows).filter(
    (row) => {
      // filter out rows that don't match the filter, except for the column that is being filtered
      // this is to prevent the filter from being applied to itself and causing a wrong result count
      return Object.keys(row.columnFilters)
        .filter((key) => key !== props.column?.id)
        .every((key) => row.columnFilters[key] === true);
    },
  );

  return (
    <Popover>
      {({ open, close }) => (
        <>
          <span
            ref={(node) => {
              refs.setReference(node);
            }}
          >
            <Popover.Button
              as={Button}
              theme="subtle"
              compact
              data-testid={`filter-button-${props.column?.id}`}
              className={clsx(
                open ? 'bg-neutral-10' : 'bg-transparent',
                'text-2xs -translate-x-1 translate-y-1 cursor-pointer',
                'font-semibold uppercase text-neutral-100',
                'kunde:bg-inherit md:kunde:p-1.5 group',
                'kunde:typ-headline-sm kunde:text-nowrap kunde:normal-case kunde:text-neutral-60',
                'kunde:hover:bg-blue-10 kunde:hover:text-neutral-100 kunde:active:bg-blue-10',
              )}
              icon={
                open
                  ? isKunde
                    ? 'chevron-down'
                    : 'chevron-down-large'
                  : isKunde
                    ? 'chevron-right'
                    : 'chevron-right-large'
              }
              iconProps={{
                size: isKunde ? 'medium' : 'tiny',
                className: 'kunde:-mr-1 kunde:-ml-2 pr-1',
              }}
            >
              <span className="kunde:text-neutral-100 kunde:group-hover:text-blue-100">
                {props.label}{' '}
                {Array.isArray(values) && values.length > 0
                  ? `(${values.length})`
                  : (typeof values === 'string' && values) || ''}
              </span>
            </Popover.Button>
          </span>

          <LazyAnimatePresence>
            {open ? (
              <FloatingPortal>
                <Popover.Panel focus>
                  <m.div
                    data-testid={`column-filter-popover-${props.column?.id}`}
                    className={clsx(
                      'bg-neutral-0 kunde:rounded-md absolute z-20 w-max rounded p-2 py-1 shadow-lg ring-offset-0 focus:ring-0',
                      'min-w-60',
                      // kunde mobile fullscreen
                      'kunde:max-md:!fixed kunde:max-md:inset-0 kunde:max-md:!transform-none kunde:max-md:!w-screen kunde:max-md:!h-screen kunde:max-md:!z-[100]',
                    )}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 2 }}
                    exit={{ opacity: 1 }}
                    transition={{ duration: 1.1 }}
                    ref={refs.setFloating}
                    style={{
                      ...floatingStyles,
                      width: props.column?.getSize(),
                    }}
                  >
                    {match(props.column?.columnDef)
                      .with(
                        { filterFn: 'arrIncludesSome' },
                        { filterFn: 'arrIncludes' },
                        { filterFn: 'arrIncludesAll' },
                        { meta: { filterVariant: 'select' } },
                        () => (
                          <CheckboxFilter
                            filteredRows={filteredRows}
                            close={close}
                            data={props.data}
                            column={props.column}
                            label={props.label}
                          />
                        ),
                      )
                      .otherwise((value) => {
                        throw TypeError(`Unsupported filterFn: "${value}"`);
                      })}
                  </m.div>
                </Popover.Panel>
              </FloatingPortal>
            ) : null}
          </LazyAnimatePresence>
        </>
      )}
    </Popover>
  );
}
